import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiPlus } from "@mdi/js";
import PageLayout from "app/components/common/PageLayout";
import HowItWorksButton from "app/components/common/HowItWorksButton";
import CustomToast from "app/components/common/CustomToast";
import { useApp } from "app/contexts/AppContext";
import { useInitialFetch } from "app/hooks/useInitialFetch";
import {
  fetchContracts,
  createContract,
  duplicateContract,
  renameContract,
  setContractAsDefault,
  deleteContract,
  clearCreateStatus,
  clearDuplicateStatus,
  clearRenameStatus,
  clearSetDefaultStatus,
  clearDeleteStatus,
} from "./slice";
import ContractRow from "./ContractRow";
import ContractCard from "./ContractCard";
import CalloutBanner from "./CalloutBanner";
import CreateContractModal from "./CreateContractModal";
import RenameContractModal from "./RenameContractModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import ContractHowItWorksModal from "./ContractHowItWorksModal";
import { useFlashToast } from "./useFlashToast";
import "./contractManagement.css";

const ContractManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    device: { isMobile },
    user: { profile: userProfile },
  } = useApp();
  // The upsell banner is a free-tier promotion only. A free user has either no
  // subscription record (null) or an explicit "Free" tier — the app defaults a
  // missing subscription to Free (see GlobalNavBar UserMenu).
  const subscriptionTier = userProfile?.subscription?.tier;
  const isFreeTier = !subscriptionTier || subscriptionTier === "Free";

  const {
    templates,
    getTemplatesStatus,
    getTemplatesError,
    createTemplateStatus,
    createdTemplateId,
    duplicateTemplateStatus,
    duplicatedTemplateId,
    renameTemplateStatus,
    setDefaultStatus,
    deleteTemplateStatus,
    deletedFallbackName,
  } = useSelector((state) => state.contractManagement);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  // Name of the template the user just promoted, so the toast can name it.
  const [defaultName, setDefaultName] = useState("");

  const { toast, showToast, hideToast, navigateWithToast } = useFlashToast();

  // Sticky header shadow when scrolled
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setIsHeaderSticky(window.pageYOffset > 100);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useInitialFetch(() => dispatch(fetchContracts()), {
    skip: templates.length > 0,
  });

  // After create — open the new template in edit mode. The toast rides along in
  // navigation state so it renders on the editor (this page unmounts).
  useEffect(() => {
    if (createTemplateStatus === "success" && createdTemplateId !== null) {
      const newId = createdTemplateId;
      const created = templates.find((t) => String(t.id) === String(newId));
      setShowCreateModal(false);
      dispatch(clearCreateStatus());
      navigateWithToast(
        `/contracts/${newId}?mode=edit`,
        `"${created?.name || "Template"}" created successfully.`
      );
    }
  }, [
    createTemplateStatus,
    createdTemplateId,
    templates,
    dispatch,
    navigateWithToast,
  ]);

  // After duplicate — open duplicate in view mode, carrying the toast across.
  useEffect(() => {
    if (
      duplicateTemplateStatus === "success" &&
      duplicatedTemplateId !== null
    ) {
      const newId = duplicatedTemplateId;
      dispatch(clearDuplicateStatus());
      navigateWithToast(
        `/contracts/${newId}`,
        "Template duplicated successfully."
      );
    }
  }, [
    duplicateTemplateStatus,
    duplicatedTemplateId,
    dispatch,
    navigateWithToast,
  ]);

  // Rename toast
  useEffect(() => {
    if (renameTemplateStatus === "success") {
      showToast("Template renamed successfully.");
      setRenameTarget(null);
      dispatch(clearRenameStatus());
    }
  }, [renameTemplateStatus, dispatch, showToast]);

  // Set default toast
  useEffect(() => {
    if (setDefaultStatus === "success") {
      showToast(
        defaultName
          ? `"${defaultName}" is now your default.`
          : "Default template updated."
      );
      dispatch(clearSetDefaultStatus());
    }
  }, [setDefaultStatus, defaultName, dispatch, showToast]);

  // Delete toast (with auto-fallback note when applicable)
  useEffect(() => {
    if (deleteTemplateStatus === "success") {
      const name = deleteTarget?.name;
      const base = name ? `Template "${name}" deleted.` : "Template deleted.";
      const msg = deletedFallbackName
        ? `${base} "${deletedFallbackName}" is now your default.`
        : base;
      showToast(msg);
      setDeleteTarget(null);
      dispatch(clearDeleteStatus());
    }
  }, [
    deleteTemplateStatus,
    deletedFallbackName,
    deleteTarget,
    dispatch,
    showToast,
  ]);

  const handleOpen = (template) => navigate(`/contracts/${template.id}`);
  const handleSetDefault = (template) => {
    setDefaultName(template.name);
    dispatch(setContractAsDefault(template.id));
  };
  const handleDuplicate = (template) =>
    dispatch(duplicateContract(template.id));

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    dispatch(deleteContract(deleteTarget.id));
  };

  const handleConfirmCreate = (name) => {
    dispatch(createContract({ name }));
  };

  const handleConfirmRename = (name) => {
    if (!renameTarget) return;
    dispatch(renameContract({ contractId: renameTarget.id, name }));
  };

  const headerActions = (
    <>
      <HowItWorksButton onClick={() => setShowHowItWorks(true)} />
      <Button variant="primary" onClick={() => setShowCreateModal(true)}>
        <Icon path={mdiPlus} size={0.8} className="me-1" />
        New Template
      </Button>
    </>
  );

  const isLoading = getTemplatesStatus === "loading" && templates.length === 0;
  const hasError = getTemplatesStatus === "failed" && templates.length === 0;
  const isEmpty = getTemplatesStatus === "success" && templates.length === 0;
  const handleRetry = () => dispatch(fetchContracts());

  // A 5xx can come back as a raw HTML error page; don't dump that into the UI.
  const errorDetail =
    typeof getTemplatesError === "string" &&
    !/^\s*<(?:!doctype|html)/i.test(getTemplatesError)
      ? getTemplatesError
      : "The server returned an error. Please try again, or contact support if it keeps happening.";

  return (
    <>
      <PageLayout
        className="contract-management-page"
        title="Contract Management"
        headerActions={headerActions}
        stickyHeader={isHeaderSticky}
        banner={isFreeTier ? <CalloutBanner isMobile={isMobile} /> : undefined}
      >
        {isLoading ? (
          <div className="contract-loading">
            <div className="contract-loading-spinner" />
          </div>
        ) : hasError ? (
          <div className="contract-state contract-state-error">
            <p className="contract-state-title">
              We couldn't load your contract templates.
            </p>
            <p className="contract-state-detail">{errorDetail}</p>
            <button
              type="button"
              className="contract-create-btn"
              onClick={handleRetry}
            >
              Try Again
            </button>
          </div>
        ) : isEmpty ? (
          <div className="contract-state">
            <p className="contract-state-title">No templates yet.</p>
            <p className="contract-state-detail">
              Create your first contract template to get started.
            </p>
          </div>
        ) : isMobile ? (
          <div className="contract-cards">
            {templates.map((t) => (
              <ContractCard
                key={t.id}
                template={t}
                onOpen={() => handleOpen(t)}
                onSetDefault={() => handleSetDefault(t)}
                onDelete={() => setDeleteTarget(t)}
                onDuplicate={() => handleDuplicate(t)}
              />
            ))}
          </div>
        ) : (
          <div className="contract-table-wrapper">
            <Table className="contract-table">
              <thead>
                <tr>
                  <th>Contract Name</th>
                  <th style={{ width: 200 }}>Last Modified</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <ContractRow
                    key={t.id}
                    template={t}
                    onOpen={() => handleOpen(t)}
                    onSetDefault={() => handleSetDefault(t)}
                    onDelete={() => setDeleteTarget(t)}
                    onDuplicate={() => handleDuplicate(t)}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </PageLayout>

      <CreateContractModal
        show={showCreateModal}
        isCreating={createTemplateStatus === "loading"}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleConfirmCreate}
      />

      <RenameContractModal
        show={!!renameTarget}
        initialName={renameTarget?.name || ""}
        onClose={() => setRenameTarget(null)}
        onSubmit={handleConfirmRename}
      />

      <DeleteConfirmationModal
        show={!!deleteTarget}
        templateName={deleteTarget?.name || ""}
        isDeleting={deleteTemplateStatus === "loading"}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      <ContractHowItWorksModal
        show={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
      />

      <CustomToast
        show={toast.show}
        onClose={hideToast}
        message={toast.message}
        variant={toast.variant}
        position="bottom-end"
        isMobile={isMobile}
        autohide
      />
    </>
  );
};

export default ContractManagement;
